/**
 * Maps numeric taskId to stable string taskKey for API calls
 */
export const mapTaskIdToKey = (taskId: number | string): string => {
  const id = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
  
  // Map taskId to stable taskKey strings
  switch (id) {
    // Personal tasks
    case 1:
      return 'identifying_info';
    case 2:
      return 'employment_info';
    case 3:
      return 'spouse_info';
    case 4:
      return 'children_info';
    case 5:
      return 'pet_info';
    // Medical tasks
    case 6:
      return 'primary_physician_info';
    case 7:
      return 'specialty_physicians';
    case 8:
      return 'medical_history';
    case 9:
      return 'current_medications';
    case 10:
      return 'caregiver_info';
    case 11:
      return 'medical_equipment';
    case 12:
      return 'organ_donation';
    case 13:
      return 'current_therapy';
    // Finance tasks
    case 46:
      return 'accountant';
    case 47:
      return 'attorney';
    case 48:
      return 'investment_advisor';
    case 49:
      return 'insurance_agent';
    case 50:
      return 'tax_advisor';
    case 51:
      return 'credit_cards';
    case 52:
      return 'banking';
    case 53:
      return 'investments';
    case 54:
      return 'loans';
    case 55:
      return 'crypto';
    // Property tasks
    case 82:
      return 'home_ownership';
    case 83:
      return 'homeowners_insurance';
    case 85:
      return 'cellphone';
    case 86:
      return 'home_inventory';
    case 87:
      return 'gas_utility';
    case 88:
      return 'electricity';
    case 89:
      return 'waste';
    case 90:
      return 'water_sewer';
    case 91:
      return 'home_security';
    case 92:
      return 'cable_streaming';
    case 93:
      return 'lawn_service';
    case 94:
      return 'pest_control';
    case 95:
      return 'pool_service';
    case 96:
      return 'gym_membership';
    // Final Wishes tasks
    case 64:
      return 'body_arrangements';
    case 65:
      return 'funeral_home';
    case 66:
      return 'casket_urn';
    case 67:
      return 'memorial_service';
    case 68:
      return 'tribute_donations';
    case 69:
      return 'funeral_instructions';
    case 70:
      return 'officiant';
    case 71:
      return 'letter_to_loved_ones';
    case 72:
      return 'prayers_readings';
    case 73:
      return 'food_preferences';
    case 74:
      return 'music';
    case 75:
      return 'pallbearers';
    case 76:
      return 'traditions_rituals';
    case 77:
      return 'wake_details';
    case 78:
      return 'eulogy';
    case 79:
      return 'burial_plot';
    case 80:
      return 'obituary_details';
    case 81:
      return 'headstone_details';
    // Final Care tasks
    case 56:
      return 'advance_directive';
    case 57:
      return 'ltc_preferences';
    case 58:
      return 'dnr_preferences';
    case 59:
      return 'breathing_tube_preferences';
    case 60:
      return 'feeding_tube_preferences';
    case 61:
      return 'medical_poa_preferences';
    case 62:
      return 'pet_care_preferences';
    case 63:
      return 'living_trust_preferences';
    // Healthcare Directives (new IDs)
    case 500:
      return 'hipaa_authorization';
    // Estate Planning (new IDs)
    case 501:
      return 'last_will';
    case 502:
      return 'financial_poa';
    case 503:
      return 'beneficiary_designations';
    // Care Preferences (new IDs)
    case 504:
      return 'end_of_life_wishes';
    default:
      return 'identifying_info'; // Default fallback for personal info
  }
};

/**
 * Reverse mapping: taskKey to taskId
 */
export const mapTaskKeyToId = (taskKey: string): number => {
  switch (taskKey) {
    // Personal tasks
    case 'identifying_info':
      return 1;
    case 'employment_info':
      return 2;
    case 'spouse_info':
      return 3;
    case 'children_info':
      return 4;
    case 'pet_info':
      return 5;
    // Medical tasks
    case 'primary_physician_info':
      return 6;
    case 'specialty_physicians':
      return 7;
    case 'medical_history':
      return 8;
    case 'current_medications':
      return 9;
    case 'caregiver_info':
      return 10;
    case 'medical_equipment':
      return 11;
    case 'organ_donation':
      return 12;
    case 'current_therapy':
      return 13;
    // Finance tasks
    case 'accountant':
      return 46;
    case 'attorney':
      return 47;
    case 'investment_advisor':
      return 48;
    case 'insurance_agent':
      return 49;
    case 'tax_advisor':
      return 50;
    case 'credit_cards':
      return 51;
    case 'banking':
      return 52;
    case 'investments':
      return 53;
    case 'loans':
      return 54;
    case 'crypto':
      return 55;
    // Property tasks
    case 'home_ownership':
      return 82;
    case 'homeowners_insurance':
      return 83;
    case 'cellphone':
      return 85;
    case 'home_inventory':
      return 86;
    case 'gas_utility':
      return 87;
    case 'electricity':
      return 88;
    case 'waste':
      return 89;
    case 'water_sewer':
      return 90;
    case 'home_security':
      return 91;
    case 'cable_streaming':
      return 92;
    case 'lawn_service':
      return 93;
    case 'pest_control':
      return 94;
    case 'pool_service':
      return 95;
    case 'gym_membership':
      return 96;
    // Final Wishes tasks
    case 'body_arrangements':
      return 64;
    case 'funeral_home':
      return 65;
    case 'casket_urn':
      return 66;
    case 'memorial_service':
      return 67;
    case 'tribute_donations':
      return 68;
    case 'funeral_instructions':
      return 69;
    case 'officiant':
      return 70;
    case 'letter_to_loved_ones':
      return 71;
    case 'prayers_readings':
      return 72;
    case 'food_preferences':
      return 73;
    case 'music':
      return 74;
    case 'pallbearers':
      return 75;
    case 'traditions_rituals':
      return 76;
    case 'wake_details':
      return 77;
    case 'eulogy':
      return 78;
    case 'burial_plot':
      return 79;
    case 'obituary_details':
      return 80;
    case 'headstone_details':
      return 81;
    // Final Care tasks
    case 'advance_directive':
      return 56;
    case 'ltc_preferences':
      return 57;
    case 'dnr_preferences':
      return 58;
    case 'breathing_tube_preferences':
      return 59;
    case 'feeding_tube_preferences':
      return 60;
    case 'medical_poa_preferences':
      return 61;
    case 'pet_care_preferences':
      return 62;
    case 'living_trust_preferences':
      return 63;
    // Healthcare Directives (new IDs)
    case 'hipaa_authorization':
      return 500;
    // Estate Planning (new IDs)
    case 'last_will':
      return 501;
    case 'financial_poa':
      return 502;
    case 'beneficiary_designations':
      return 503;
    // Care Preferences (new IDs)
    case 'end_of_life_wishes':
      return 504;
    default:
      return 1; // Default to identifying_info
  }
};